import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Sailboat, CircleDot, Trophy, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

// Sport → icon. Falls back to Trophy for anything we don't recognise.
const ICON_BY_SPORT = {
  "Zeilen": Sailboat,
  "Tennis": CircleDot,
  "Hockey": Trophy,
};

/**
 * TRIAL-friendly dialog that lets users kick off the AI extraction against
 * one of the shipped demo annual reports (zeilclub/tennisclub/hockeyclub).
 * On selection it POSTs /clubs/{clubId}/extract-demo-pdf which returns the
 * same shape as the regular upload, so the caller can hand the response into
 * the existing preview flow.
 */
export default function DemoSampleDialog({ open, onOpenChange, clubId, onExtracted }) {
  const t = useT();
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get("/demo-annual-reports")
      .then(({ data }) => { if (!cancelled) setSamples(data); })
      .catch((e) => { if (!cancelled) toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const pick = async (sample) => {
    setPending(sample.id);
    try {
      const { data } = await api.post(`/clubs/${clubId}/extract-demo-pdf`, { sample_id: sample.id });
      // Hand the raw extraction payload back to the parent so its existing
      // preview flow can render it exactly like a user-uploaded PDF.
      onExtracted?.(data, sample);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || String(e.message || e));
    } finally {
      setPending(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="demo-sample-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2 pr-8">
            <Sparkles className="w-5 h-5" /> {t("Demo-jaarverslag kiezen")}
          </DialogTitle>
          <DialogDescription>
            {t("In de proefperiode kun je één van deze fictieve jaarverslagen laden om de AI-import te zien werken. Je eigen jaarverslag kun je uploaden na upgrade naar STANDARD of PREMIUM.")}
          </DialogDescription>
        </DialogHeader>
        {loading && (
          <div className="p-6 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("Bezig met laden…")}
          </div>
        )}
        {!loading && samples.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-4 mt-2" data-testid="demo-sample-list">
            {samples.map((s) => {
              const Icon = ICON_BY_SPORT[s.sport] || Trophy;
              const busy = pending === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => pick(s)}
                  disabled={busy || Boolean(pending)}
                  data-testid={`demo-sample-${s.id}`}
                  className="text-left border-2 border-foreground heavy-shadow bg-card p-4 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_hsl(var(--shadow-color))] transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-5 h-5" />
                    <div className="stat-label">{s.sport}</div>
                  </div>
                  <div className="font-display font-black uppercase tracking-tight text-lg mb-1">{s.name}</div>
                  <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-2">
                    <MapPin className="w-3 h-3" /> {s.location}
                  </div>
                  <p className="text-xs text-foreground/80 line-clamp-3">{s.description}</p>
                  {busy && (
                    <div className="mt-3 inline-flex items-center gap-2 text-xs text-primary font-bold">
                      <Loader2 className="w-3 h-3 animate-spin" /> {t("AI leest het jaarverslag…")}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
        <DialogFooter className="mt-4">
          <button
            onClick={() => onOpenChange(false)}
            disabled={Boolean(pending)}
            data-testid="demo-sample-cancel"
            className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold"
          >
            {t("Annuleren")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
